import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../services/auth.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule,
  ],
  template: `
    <div class="auth-page">
      <mat-card class="auth-card">
        <div class="auth-logo">
          <img src="assets/NewLogo.webp" alt="Chareety">
          <h2>Welcome Back</h2>
          <p>Continue your nonprofit onboarding</p>
        </div>

        <form [formGroup]="loginForm" (ngSubmit)="onSubmit()">
          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Email</mat-label>
              <input matInput formControlName="email" type="email" placeholder="your.email@nonprofit.org">
              <mat-error *ngIf="hasError('email', 'required')">Email is required</mat-error>
              <mat-error *ngIf="hasError('email', 'email')">Enter a valid email</mat-error>
            </mat-form-field>
          </div>

          <div class="form-group">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Password</mat-label>
              <input matInput formControlName="password" type="password" placeholder="Your password">
              <mat-error *ngIf="hasError('password', 'required')">Password is required</mat-error>
            </mat-form-field>
          </div>

          <button mat-raised-button color="primary" type="submit" class="full-width" [disabled]="loading || !loginForm.valid">
            <mat-spinner *ngIf="loading" diameter="20" class="btn-spinner"></mat-spinner>
            <span>{{ loading ? 'Signing in...' : 'Sign In' }}</span>
          </button>
        </form>

        <div *ngIf="!resetMode" style="text-align:center;margin-top:8px">
          <button mat-button color="primary" (click)="resetMode = true" style="font-size:13px">Forgot your password?</button>
        </div>

        <div *ngIf="resetMode" style="padding:16px 0">
          <p style="font-size:14px;color:#555;margin-bottom:12px">Enter your email to receive a password reset link.</p>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Email</mat-label>
            <input matInput type="email" [(ngModel)]="resetEmail">
          </mat-form-field>
          <div style="display:flex;gap:8px">
            <button mat-flat-button color="primary" (click)="sendReset()" [disabled]="resetLoading">
              <mat-spinner *ngIf="resetLoading" diameter="18" style="display:inline-block;margin-right:8px"></mat-spinner>
              Send Reset Link
            </button>
            <button mat-stroked-button (click)="resetMode = false">Cancel</button>
          </div>
        </div>

        <div class="auth-link-row">
          Don't have an account? <a routerLink="/register">Create one</a>
        </div>
      </mat-card>
    </div>
  `,
  styles: [`
    .auth-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg,
        var(--chareety-primary) 0%,
        var(--chareety-primary-dark) 100%);
      padding: 24px;
    }

    .auth-card {
      width: 100%;
      max-width: 420px;
      padding: 32px 28px 24px;
      border-radius: 14px !important;
      box-shadow: 0 8px 32px rgba(0,0,0,.18) !important;
      border-top: 4px solid var(--chareety-accent);
    }

    .auth-logo {
      text-align: center;
      margin-bottom: 28px;

      img { height: 40px; }

      h2 {
        font-size: 22px;
        font-weight: 700;
        color: var(--chareety-text);
        margin: 10px 0 4px;
      }

      p {
        font-size: 13px;
        color: var(--chareety-muted);
        margin: 0;
      }
    }

    .form-group {
      margin-bottom: 16px;
    }

    .full-width {
      width: 100%;
    }

    button {
      margin-top: 8px;
      height: 44px;
    }

    .btn-spinner {
      display: inline-block;
      margin-right: 8px;
    }

    .auth-link-row {
      text-align: center;
      font-size: 13px;
      color: var(--chareety-muted);
      margin-top: 12px;

      a { color: var(--chareety-accent-dark); font-weight: 500; }
    }
  `]
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  loading = false;
  resetMode = false;
  resetEmail = '';
  resetLoading = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private api: OnboardingApiService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  /** Safe template helper — avoids optional chaining in templates */
  hasError(controlName: string, errorName: string): boolean {
    const control = this.loginForm.get(controlName);
    return control !== null && control.hasError(errorName);
  }

  ngOnInit(): void {
    // Pre-fill email if coming from register
    this.route.queryParams.subscribe(params => {
      if (params['email']) {
        this.loginForm.patchValue({ email: params['email'] });
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (!this.loginForm.valid) return;

    this.loading = true;
    try {
      const { email, password } = this.loginForm.value;
      const user = await this.authService.login(email, password);

      console.log('Logged in user UID:', user.uid);

      // Fetch user's organizations using Cloud Function
      const orgs = await this.api.getUserOrganizations(user.uid).toPromise();

      console.log('Organizations found:', orgs);

      if (!orgs || orgs.length === 0) {
        this.snackBar.open('No organizations found for UID ' + user.uid, 'Close', { duration: 10000 });
        await this.router.navigate(['/register']);
        return;
      }

      // For now, redirect to first organization's onboarding
      // In future: show org selector if user has multiple orgs
      const orgId = orgs[0].id;
      console.log('Navigating to onboarding for org:', orgId);
      await this.router.navigate(['/onboard', orgId]);
    } catch (error: any) {
      console.error('Login error:', error);
      this.snackBar.open(error.message || 'Login failed: ' + JSON.stringify(error), 'Close', { duration: 10000 });
    } finally {
      this.loading = false;
    }
  }

  async sendReset(): Promise<void> {
    if (!this.resetEmail) return;
    this.resetLoading = true;
    try {
      await this.authService.sendPasswordReset(this.resetEmail);
      this.snackBar.open('Password reset email sent! Check your inbox.', 'OK', { duration: 5000 });
      this.resetMode = false;
    } catch (err: any) {
      this.snackBar.open('Could not send reset email. Please check your email address.', 'OK', { duration: 4000 });
    } finally {
      this.resetLoading = false;
    }
  }
}
